"use client";

import { useState, useMemo, useEffect, startTransition } from "react";
import Image from "next/image";
import Link from "next/link";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useActionState } from "react";
import { createSubscriberAction } from "@/actions/subscriberActions";
import { EventListResponse } from "@/types/event";
import { Calendar, Tag, TrendingUp, FileText } from "lucide-react";

interface FieldErrors {
  [key: string]: string[];
}

interface EventsPageProps {
  events: EventListResponse;
}

export default function EventsPage({ events }: EventsPageProps) {
  const [recentVisible, setRecentVisible] = useState(3);

  const [email, setEmail] = useState("");
  const [formState, dispatch, isPending] = useActionState(createSubscriberAction, {});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const errorFor = (field: string) => {
    return formState.error &&
      typeof formState.error === "object" &&
      field in formState.error
      ? (formState.error as FieldErrors)[field]?.[0]
      : null;
  };

  useEffect(() => {
    if (formState.data && !formState.error) {
      setDialogMessage("You have been successfully subscribed!");
      setDialogOpen(true);
      setEmail("");
    }

    if (formState.error && "message" in formState.error) {
      setDialogMessage((formState.error as any).message?.[0] || "Something went wrong!");
      setDialogOpen(true);
    }
  }, [formState]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA; // Newest first
    });
  }, [events]);

  const featured = sortedEvents.slice(0, 3);
  const recent = sortedEvents.slice(0, recentVisible);
  const hasEvents = sortedEvents.length > 0;

  return (
    <div className="text-gray-800">
      {/* Hero Section */}
      <section className="bg-purple-800 text-white text-center py-16 sm:py-20 px-4">
        {/* 🔹 Main Heading */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 sm:mb-6">
          ITSCoupons Events
        </h1>

        {/* 🔹 Main Description */}
        <p className="text-sm sm:text-base max-w-2xl mx-auto mb-6 sm:mb-8">
          Discover exclusive events, limited-time promotions, and special offers
          from your favorite stores. Stay ahead of the latest deals and never miss
          out on amazing savings opportunities.
        </p>

        {/* 🔹 Icon Highlights */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 mt-4 sm:mt-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <Calendar className="w-6 h-6 sm:w-7 sm:h-7" />
            <span className="text-sm sm:text-base font-medium">Upcoming Events</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Tag className="w-6 h-6 sm:w-7 sm:h-7" />
            <span className="text-sm sm:text-base font-medium">Special Promotions</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <TrendingUp className="w-6 h-6 sm:w-7 sm:h-7" />
            <span className="text-sm sm:text-base font-medium">Limited Offers</span>
          </div>
        </div>
      </section>

      {/* Featured Events */}
      <section className="py-10 px-4 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-6">Featured Events</h2>
        { !hasEvents ? (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="rounded-[24px] border border-purple-100 bg-white shadow-[0_12px_40px_rgba(91,33,182,0.08)] px-6 py-10 sm:px-10 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100 text-purple-700">
                <FileText className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">No events found</h3>
              <p className="mt-3 text-sm sm:text-base text-gray-600 max-w-lg mx-auto leading-relaxed">
                We couldn&apos;t find any events right now. Please check back soon for upcoming events and promotions.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="/" className="bg-purple-700 hover:bg-purple-800 text-white px-5 py-2.5 rounded-full">
                  Go Home
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {featured.map((event, index) => {
              const formattedDate = event.date
                ? new Date(event.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "";

              return (
                <div
                  key={event._id}
                  className="flex flex-col bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  {/* Image */}
                  <div className="w-full h-48 bg-gray-200 overflow-hidden flex items-center justify-center">
                    {event.image ? (
                      <Image
                        src={`https://www.itscoupons.com${event.image}`}
                        alt={event.title}
                        width={400}
                        height={200}
                        priority={index < 3}
                        loading={index < 3 ? "eager" : "lazy"}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <span className="text-gray-400 text-sm">No Image</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 flex flex-col justify-between flex-1">
                    <div>
                      <p className="text-xs text-gray-500 mb-2">{formattedDate}</p>
                      <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                        {event.title}
                      </h3>
                      {event.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                          {event.description.replace(/<[^>]*>/g, "").substring(0, 100)}...
                        </p>
                      )}
                    </div>
                    {event.slug && (
                      <Link
                        href={`/events/${event.slug}`}
                        className="text-purple-700 font-semibold hover:underline flex items-center text-sm mt-auto"
                      >
                        Read More <span className="ml-1 text-purple-700">→</span>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Recent Events */}
      {hasEvents && (
      <section className="py-10 px-4 max-w-5xl mx-auto">
        <h3 className="text-lg sm:text-xl font-semibold mb-6">Recent Events</h3>
        <div className="flex flex-col gap-6">
          {recent.map((event) => (
            <Link key={event._id} href={event.slug ? `/events/${event.slug}` : "#"}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center border-l-4 border-purple-600 pl-4 pb-4 hover:bg-gray-50 transition rounded-md cursor-pointer gap-3">
                {event.image && (
                  <div className="relative w-full sm:w-32 h-24 flex-shrink-0">
                    <Image
                      src={`https://www.itscoupons.com${event.image}`}
                      alt={event.title}
                      title={event.title}
                      width={128}
                      height={96}
                      sizes="(max-width: 640px) 100vw, 200px"
                      className="object-cover rounded-md"
                      loading="lazy"
                    />
                  </div>
                )}

                <div className="text-sm sm:text-base flex-1">
                  <p className="font-medium text-purple-700 mb-1">
                    {event.date
                      ? new Date(event.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "No date"}
                  </p>
                  <p className="font-semibold">{event.title}</p>
                  {event.description && (
                    <p className="text-xs sm:text-sm text-gray-500 line-clamp-1 mt-1">
                      {event.description.replace(/<[^>]*>/g, "").substring(0, 150)}...
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {recentVisible < sortedEvents.length && (
          <div className="mt-8 text-center">
            <Button
              onClick={() => setRecentVisible((prev) => prev + 3)}
              className="bg-purple-700 hover:bg-purple-800 text-white px-6 py-2 rounded"
            >
              Load More Events
            </Button>
          </div>
        )}
      </section>
      )}

      {/* Newsletter */}
      <section className="bg-white text-center py-12 px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">Stay Updated with Events</h2>
        <p className="text-gray-600 max-w-xl mx-auto mb-6">
          Get notified about upcoming events, exclusive promotions, and limited-time offers.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData();
            formData.set("email", email);
            startTransition(() => {
              dispatch(formData);
            });
          }}
          className="flex flex-col sm:flex-row justify-center items-center gap-4 max-w-md mx-auto"
        >
          <Input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="px-4 py-2 w-full sm:w-auto border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <Button
            type="submit"
            className="bg-purple-700 text-white px-6 py-2 rounded-md hover:bg-purple-800 transition shadow-sm w-full sm:w-auto"
            disabled={isPending}
          >
            {isPending ? "Subscribing..." : "Subscribe"}
          </Button>
        </form>

        {errorFor("email") && <p className="text-red-500 mt-2">{errorFor("email")}</p>}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md mx-4 sm:mx-auto animate-fade-in">
            <DialogHeader className="relative">
              <DialogTitle className="text-lg sm:text-xl">Subscription Status</DialogTitle>
            </DialogHeader>
            <p className="py-2 text-sm sm:text-base">{dialogMessage}</p>
            <DialogFooter>
              <Button
                className="bg-purple-700 text-white px-6 py-2 rounded-md hover:bg-purple-800 transition shadow-sm"
                onClick={() => setDialogOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </div>
  );
}

